import { useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

import { useMount } from "app/custom-hooks/useMount";
import ErrorBoundaryWithLogging from "app/pages/sales-intelligence/common-components/ErrorBoundaryWithLogging/ErrorBoundaryWithLogging";
import ListPageHeader from "app/pages/sales-intelligence/common-components/header/ListPageHeader/ListPageHeader";
import {
    PageInnerContainer,
    StyledPageContainer,
} from "app/pages/sales-intelligence/common-components/layout/page-styles";
import ListNotFoundModalContainer from "app/pages/sales-intelligence/common-components/modals/ListNotFoundModal/ListNotFoundModalContainer";
import ScrollToTopWrapper from "app/pages/sales-intelligence/common-components/ScrollToTopWrapper/ScrollToTopWrapper";
import { STATIC_LIST_PAGE_ROUTE } from "app/pages/sales-intelligence/constants/routes";
import withSecondaryBarSet from "app/pages/sales-intelligence/hoc/withSecondaryBarSet";
import ListTabsContainer from "app/pages/sales-intelligence/modules/lists/containers/ListTabsContainer/ListTabsContainer";
import { listsQueryKeys } from "app/pages/sales-intelligence/modules/lists/sub-modules/my-list/services/queries/queryKeyFactory";
import ListSectionLoaders from "app/pages/sales-intelligence/pages/lists/components/common/ListsSection/ListSectionLoaders";
import useCreateMyListModalInitialOpen from "app/pages/sales-intelligence/pages/lists/hooks/useCreateMyListModalInitialOpen";
import { fetchWorkspacesThunk } from "app/pages/sales-intelligence/sub-modules/common/store/effects";
import ListModalContainer from "app/pages/sales-intelligence/sub-modules/opportunities/containers/ListModal/ListModalContainer";
import { selectIsLoadingListsPage } from "app/pages/sales-intelligence/sub-modules/opportunities/store/selectors";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import MyListErrorState from "../components/my-lists/MyListErrorState/MyListErrorState";

const MyListsPageContainer = () => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    useCreateMyListModalInitialOpen();

    useMount(() => {
        void dispatch(fetchWorkspacesThunk());
    });

    const isLoadingListPage = useSelector(selectIsLoadingListsPage);

    const handleListCreate = (listId: string) => {
        proNavigator.go(STATIC_LIST_PAGE_ROUTE, {
            id: listId,
        });
        void queryClient.invalidateQueries({ queryKey: listsQueryKeys.allStaticLists() });
    };

    const renderContent = () => {
        if (isLoadingListPage) {
            return <ListSectionLoaders />;
        }

        return <ListTabsContainer />;
    };

    return (
        <ScrollToTopWrapper>
            <ErrorBoundaryWithLogging fallback={<MyListErrorState />}>
                <StyledPageContainer>
                    <PageInnerContainer>
                        <ListNotFoundModalContainer />
                        <ListModalContainer onListCreate={handleListCreate} />
                        <ListPageHeader
                            title={{ key: "si.pages.my_lists.title" }}
                            description={{ key: "si.pages.my_lists.description" }}
                        />
                        {renderContent()}
                    </PageInnerContainer>
                </StyledPageContainer>
            </ErrorBoundaryWithLogging>
        </ScrollToTopWrapper>
    );
};

export default withSecondaryBarSet("SalesIntelligenceLists")(MyListsPageContainer);
